import React, { useState, useEffect } from 'react';
import { 
  FiCheck, FiRefreshCw, FiAlertTriangle, FiChevronRight, 
  FiImage, FiLink, FiFileText, FiSettings, FiExternalLink 
} from 'react-icons/fi';
import { FaShopify } from 'react-icons/fa';

export default function AIFixesDashboard(){
  // State for active tab
  const [activeTab, setActiveTab] = useState('meta');
  // State for selected items
  const [selectedItems, setSelectedItems] = useState([]);
  // State for bulk action
  const [bulkAction, setBulkAction] = useState('');
  // State for undo history
  const [undoHistory, setUndoHistory] = useState([]);
  // State for issues data
  const [issues, setIssues] = useState({
    meta: [],
    altText: [],
    brokenLinks: [],
    content: []
  });

  // Mock data initialization
  useEffect(() => {
    // Simulate API call to get issues
    const fetchIssues = async () => {
      // In a real app, this would be an API call
      const mockIssues = {
        meta: [
          {
            id: 'meta-1',
            pageUrl: '/products/blue-shoes',
            currentTitle: 'Blue Shoes',
            currentDescription: '',
            suggestedTitle: 'Comfortable Blue Running Shoes for Men – 2024 Collection',
            suggestedDescription: 'Discover our premium blue running shoes for men. Lightweight, breathable, and perfect for all terrains. Shop now!',
            status: 'critical'
          },
          {
            id: 'meta-2',
            pageUrl: '/collections/summer',
            currentTitle: 'Summer Collection',
            currentDescription: 'Our summer products',
            suggestedTitle: 'Summer 2024 Collection: Trendy Seasonal Styles',
            suggestedDescription: 'Explore our hottest summer 2024 collection. Beachwear, sunglasses, and summer essentials for the season.',
            status: 'warning'
          }
        ],
        altText: [
          {
            id: 'alt-1',
            imageUrl: 'blue-shoes.jpg',
            product: 'Blue Running Shoes',
            currentAlt: '',
            suggestedAlt: 'Blue running shoes on wooden floor',
            status: 'warning'
          },
          {
            id: 'alt-2',
            imageUrl: 'summer-collection.jpg',
            product: 'Summer Collection Banner',
            currentAlt: '',
            suggestedAlt: 'Group of summer clothing items on beach background',
            status: 'warning'
          }
        ],
        brokenLinks: [
          {
            id: 'link-1',
            url: '/old-products',
            sourcePage: '/collections/all',
            status: 404,
            suggestedFix: '/collections/all',
            action: 'redirect'
          },
          {
            id: 'link-2',
            url: '/discounted-items',
            sourcePage: '/pages/special-offers',
            status: 404,
            suggestedFix: null,
            action: 'remove'
          }
        ],
        content: [
          {
            id: 'content-1',
            pageUrl: '/products/wireless-headphones',
            issue: 'Keyword density low for "wireless headphones"',
            suggestion: 'Add "wireless headphones" 2-3 times in product description',
            status: 'warning'
          },
          {
            id: 'content-2',
            pageUrl: '/blog',
            issue: 'Missing H2 tags with target keywords',
            suggestion: 'Add H2 section with keywords "organic cotton benefits"',
            status: 'warning'
          }
        ]
      };
      setIssues(mockIssues);
    };

    fetchIssues();
  }, []);

  // Apply fix to a single item
  const applyFix = (type, id) => {
    console.log(`Applying fix to ${type} item ${id}`);
    // In a real app, this would call the Shopify API
    // For now, we'll just update the UI state
    
    // Add to undo history
    const item = issues[type].find(item => item.id === id);
    setUndoHistory([...undoHistory, { type, id, action: 'apply', data: item }]);
    
    // Show success message
    alert(`Fix applied successfully to ${type} item ${id}`);
  };

  // Regenerate suggestion
  const regenerateSuggestion = (type, id) => {
    console.log(`Regenerating suggestion for ${type} item ${id}`);
    // In a real app, this would call the AI API
    
    // For demo, we'll just modify the existing suggestion
    const updatedIssues = {...issues};
    const itemIndex = updatedIssues[type].findIndex(item => item.id === id);
    
    if (type === 'meta') {
      updatedIssues[type][itemIndex].suggestedTitle = 
        `New ${updatedIssues[type][itemIndex].currentTitle} Suggestion (Regenerated)`;
    } else if (type === 'altText') {
      updatedIssues[type][itemIndex].suggestedAlt = 
        `New alt text for ${updatedIssues[type][itemIndex].product} (Regenerated)`;
    }
    
    setIssues(updatedIssues);
  };

  // Apply bulk action
  const applyBulkAction = () => {
    if (!bulkAction || selectedItems.length === 0) return;
    
    console.log(`Applying ${bulkAction} to ${selectedItems.length} items`);
    // In a real app, this would call the Shopify API
    
    // Add to undo history
    setUndoHistory([
      ...undoHistory, 
      { 
        type: activeTab, 
        action: 'bulk', 
        count: selectedItems.length,
        bulkAction 
      }
    ]);
    
    // Show success message
    alert(`Applied ${bulkAction} to ${selectedItems.length} items`);
    
    // Reset selection
    setSelectedItems([]);
    setBulkAction('');
  };

  // Undo last action
  const undoLastAction = () => {
    if (undoHistory.length === 0) return;
    
    const lastAction = undoHistory[undoHistory.length - 1];
    console.log('Undoing last action:', lastAction);
    
    // In a real app, this would reverse the API changes
    // For demo, we'll just show a message
    alert(`Undid ${lastAction.action} action on ${lastAction.type}`);
    
    // Remove from history
    setUndoHistory(undoHistory.slice(0, -1));
  };

  // Toggle item selection
  const toggleItemSelection = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'text-rufous-500 bg-rufous-100';
      case 'warning': return 'text-alloy_orange-500 bg-alloy_orange-100';
      default: return 'text-dark_cyan-500 bg-dark_cyan-100';
    }
  };

  return (
    <div className="min-h-screen bg-rich_black-900 text-rich_black-100">
      {/* Header */}
      <header className="bg-rich_black-800 border-b border-rich_black-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FaShopify className="text-tiffany_blue-500 text-2xl" />
            <h1 className="text-xl font-bold text-tiffany_blue-500">SEOAutoFix</h1>
          </div>
          <nav className="flex items-center space-x-6">
            <a href="#" className="text-vanilla-500 hover:text-tiffany_blue-400">Dashboard</a>
            <a href="#" className="text-vanilla-500 hover:text-tiffany_blue-400">Reports</a>
            <a href="#" className="text-vanilla-500 hover:text-tiffany_blue-400">Settings</a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Progress Bar */}
        <div className="bg-rich_black-800 rounded-lg p-6 mb-6 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-vanilla-500 mb-2">AI-Powered Fixes</h2>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor('critical')}`}>
                  {issues.meta.filter(i => i.status === 'critical').length} Critical
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor('warning')}`}>
                  {issues.meta.filter(i => i.status === 'warning').length} Warnings
                </span>
                <span className="text-vanilla-500 text-sm">
                  {issues.meta.length} issues can be fixed automatically
                </span>
              </div>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="text-vanilla-500 mb-1">SEO Health Impact</div>
              <div className="flex items-center">
                <span className="text-2xl font-bold text-rufous-500">65</span>
                <FiChevronRight className="mx-2 text-vanilla-500" />
                <span className="text-2xl font-bold text-dark_cyan-500">89</span>
                <button className="ml-6 bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 text-rich_black-900 px-4 py-2 rounded-md font-medium hover:opacity-90 transition">
                  Fix All Now
                </button>
              </div>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-rich_black-700 rounded-full h-2.5">
            <div 
              className="h-2.5 rounded-full bg-gradient-to-r from-rufous-500 to-dark_cyan-500" 
              style={{ width: '65%' }}
            ></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-rich_black-700 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('meta')}
              className={`py-4 px-1 font-medium text-sm border-b-2 ${activeTab === 'meta' ? 'border-tiffany_blue-500 text-tiffany_blue-500' : 'border-transparent text-vanilla-500 hover:text-tiffany_blue-400'}`}
            >
              <div className="flex items-center">
                <FiFileText className="mr-2" />
                Meta Tags ({issues.meta.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('altText')}
              className={`py-4 px-1 font-medium text-sm border-b-2 ${activeTab === 'altText' ? 'border-tiffany_blue-500 text-tiffany_blue-500' : 'border-transparent text-vanilla-500 hover:text-tiffany_blue-400'}`}
            >
              <div className="flex items-center">
                <FiImage className="mr-2" />
                Image Alt Text ({issues.altText.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('brokenLinks')}
              className={`py-4 px-1 font-medium text-sm border-b-2 ${activeTab === 'brokenLinks' ? 'border-tiffany_blue-500 text-tiffany_blue-500' : 'border-transparent text-vanilla-500 hover:text-tiffany_blue-400'}`}
            >
              <div className="flex items-center">
                <FiLink className="mr-2" />
                Broken Links ({issues.brokenLinks.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`py-4 px-1 font-medium text-sm border-b-2 ${activeTab === 'content' ? 'border-tiffany_blue-500 text-tiffany_blue-500' : 'border-transparent text-vanilla-500 hover:text-tiffany_blue-400'}`}
            >
              <div className="flex items-center">
                <FiSettings className="mr-2" />
                Content Optimization ({issues.content.length})
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-rich_black-800 rounded-lg shadow-lg overflow-hidden">
          {/* Meta Tags Tab */}
          {activeTab === 'meta' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-vanilla-500 mb-4">Missing/Duplicate Meta Titles & Descriptions</h3>
              
              {issues.meta.map((item) => (
                <div key={item.id} className="border border-rich_black-700 rounded-lg p-4 mb-4 bg-rich_black-750">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <a href={item.pageUrl} className="text-tiffany_blue-400 hover:underline flex items-center">
                          {item.pageUrl} <FiExternalLink className="ml-1 text-xs" />
                        </a>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-vanilla-500 mb-1">Current Title</label>
                          <div className="bg-rich_black-900 p-3 rounded border border-rich_black-700 text-vanilla-400">
                            {item.currentTitle || <span className="text-rufous-500">Missing</span>}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-vanilla-500 mb-1">AI Suggested Title</label>
                          <div className="bg-rich_black-900 p-3 rounded border border-dark_cyan-500 relative">
                            <div className="text-vanilla-400">{item.suggestedTitle}</div>
                            <div className="absolute bottom-1 right-1 text-xs text-dark_cyan-500">
                              {item.suggestedTitle.length}/60
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-vanilla-500 mb-1">Current Description</label>
                          <div className="bg-rich_black-900 p-3 rounded border border-rich_black-700 text-vanilla-400">
                            {item.currentDescription || <span className="text-rufous-500">Missing</span>}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-vanilla-500 mb-1">AI Suggested Description</label>
                          <div className="bg-rich_black-900 p-3 rounded border border-dark_cyan-500 relative">
                            <div className="text-vanilla-400">{item.suggestedDescription}</div>
                            <div className="absolute bottom-1 right-1 text-xs text-dark_cyan-500">
                              {item.suggestedDescription.length}/155
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => applyFix('meta', item.id)}
                          className="bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 text-rich_black-900 px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 transition"
                        >
                          Apply to This Page
                        </button>
                        <button
                          onClick={() => regenerateSuggestion('meta', item.id)}
                          className="flex items-center bg-rich_black-700 border border-rich_black-600 text-vanilla-500 px-4 py-2 rounded-md text-sm font-medium hover:bg-rich_black-600 transition"
                        >
                          <FiRefreshCw className="mr-2" /> Regenerate
                        </button>
                        <button className="flex items-center bg-rich_black-700 border border-rich_black-600 text-vanilla-500 px-4 py-2 rounded-md text-sm font-medium hover:bg-rich_black-600 transition">
                          <FiSettings className="mr-2" /> Customize Tone
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Alt Text Tab */}
          {activeTab === 'altText' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-vanilla-500 mb-4">Images Missing Alt Text</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {issues.altText.map((item) => (
                  <div key={item.id} className="border border-rich_black-700 rounded-lg p-4 bg-rich_black-750">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => toggleItemSelection(item.id)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="mb-3 bg-rich_black-900 rounded overflow-hidden">
                          <div className="h-40 bg-gradient-to-br from-rich_black-700 to-rich_black-800 flex items-center justify-center">
                            <FiImage className="text-4xl text-rich_black-600" />
                          </div>
                        </div>
                        
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-vanilla-500 mb-1">Product/Page</label>
                          <div className="text-tiffany_blue-400">{item.product}</div>
                        </div>
                        
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-vanilla-500 mb-1">AI Suggested Alt Text</label>
                          <div className="bg-rich_black-900 p-2 rounded border border-dark_cyan-500">
                            <div className="text-vanilla-400">{item.suggestedAlt}</div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => applyFix('altText', item.id)}
                            className="bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 text-rich_black-900 px-3 py-1 rounded-md text-sm font-medium hover:opacity-90 transition"
                          >
                            Apply
                          </button>
                          <button
                            onClick={() => regenerateSuggestion('altText', item.id)}
                            className="flex items-center bg-rich_black-700 border border-rich_black-600 text-vanilla-500 px-3 py-1 rounded-md text-sm font-medium hover:bg-rich_black-600 transition"
                          >
                            <FiRefreshCw className="mr-1" /> Regenerate
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Broken Links Tab */}
          {activeTab === 'brokenLinks' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-vanilla-500 mb-4">Broken Links Detected</h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-rich_black-700">
                  <thead className="bg-rich_black-750">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-vanilla-500 uppercase tracking-wider">
                        Select
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-vanilla-500 uppercase tracking-wider">
                        Broken URL
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-vanilla-500 uppercase tracking-wider">
                        Source Page
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-vanilla-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-vanilla-500 uppercase tracking-wider">
                        Suggested Fix
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-vanilla-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-rich_black-800 divide-y divide-rich_black-700">
                    {issues.brokenLinks.map((item) => (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => toggleItemSelection(item.id)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-vanilla-400">
                          {item.url}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-tiffany_blue-400 hover:underline">
                          <a href={item.sourcePage}>{item.sourcePage}</a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor('critical')}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-vanilla-400">
                          {item.suggestedFix || 'No suggestion available'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {item.suggestedFix && (
                              <button
                                onClick={() => applyFix('brokenLinks', item.id)}
                                className="text-dark_cyan-500 hover:text-tiffany_blue-400"
                              >
                                Create Redirect
                              </button>
                            )}
                            <button className="text-rufous-500 hover:text-alloy_orange-400">
                              Remove Link
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Content Optimization Tab */}
          {activeTab === 'content' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-vanilla-500 mb-4">Keyword & Readability Improvements</h3>
              
              {issues.content.map((item) => (
                <div key={item.id} className="border border-rich_black-700 rounded-lg p-4 mb-4 bg-rich_black-750">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <a href={item.pageUrl} className="text-tiffany_blue-400 hover:underline flex items-center">
                          {item.pageUrl} <FiExternalLink className="ml-1 text-xs" />
                        </a>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                      </div>
                      
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-vanilla-500 mb-1">Issue</label>
                        <div className="bg-rich_black-900 p-3 rounded border border-rich_black-700 text-vanilla-400">
                          {item.issue}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-vanilla-500 mb-1">AI Suggestion</label>
                        <div className="bg-rich_black-900 p-3 rounded border border-dark_cyan-500">
                          <div className="text-vanilla-400">{item.suggestion}</div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => applyFix('content', item.id)}
                          className="bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 text-rich_black-900 px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 transition"
                        >
                          Apply Suggestion
                        </button>
                        <button className="flex items-center bg-rich_black-700 border border-rich_black-600 text-vanilla-500 px-4 py-2 rounded-md text-sm font-medium hover:bg-rich_black-600 transition">
                          <FiFileText className="mr-2" /> Edit Manually
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="mt-8">
                <h4 className="text-md font-medium text-vanilla-500 mb-3">Generate Blog Post Ideas</h4>
                <div className="bg-rich_black-750 border border-rich_black-700 rounded-lg p-4">
                  <div className="flex">
                    <input
                      type="text"
                      placeholder="Enter a topic or keyword"
                      className="flex-1 bg-rich_black-900 border border-rich_black-700 rounded-l-md px-4 py-2 text-vanilla-400 focus:outline-none focus:ring-1 focus:ring-tiffany_blue-500"
                    />
                    <button className="bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 text-rich_black-900 px-4 py-2 rounded-r-md font-medium hover:opacity-90 transition">
                      Generate
                    </button>
                  </div>
                  <div className="mt-4 text-sm text-vanilla-500">
                    Example: "10 Best Blue Shoes for Running in 2024"
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Bulk Actions Footer */}
      {selectedItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-rich_black-800 border-t border-rich_black-700 p-4 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="text-vanilla-500">
              <span className="font-medium">{selectedItems.length}</span> items selected
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="bg-rich_black-900 border border-rich_black-700 text-vanilla-400 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-tiffany_blue-500"
              >
                <option value="">Select bulk action</option>
                <option value="apply">Apply fixes</option>
                <option value="ignore">Ignore issues</option>
                {activeTab === 'brokenLinks' && <option value="redirect">Create redirects</option>}
              </select>
              <button
                onClick={applyBulkAction}
                disabled={!bulkAction}
                className={`px-4 py-2 rounded-md font-medium ${bulkAction ? 'bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 text-rich_black-900 hover:opacity-90' : 'bg-rich_black-700 text-rich_black-500 cursor-not-allowed'}`}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Toast */}
      {undoHistory.length > 0 && (
        <div className="fixed bottom-20 right-6 bg-rich_black-800 border border-rich_black-700 rounded-lg shadow-lg p-4 max-w-xs">
          <div className="flex items-start">
            <div className="flex-shrink-0 text-tiffany_blue-500 mt-0.5">
              <FiCheck />
            </div>
            <div className="ml-3">
              <p className="text-sm text-vanilla-500">
                {`${undoHistory[undoHistory.length - 1].action === 'bulk' ? 
                  `Applied ${undoHistory[undoHistory.length - 1].bulkAction} to ${undoHistory[undoHistory.length - 1].count} items` : 
                  'Fix applied successfully'}`}
              </p>
              <button
                onClick={undoLastAction}
                className="mt-1 text-sm font-medium text-tiffany_blue-500 hover:text-dark_cyan-400"
              >
                Undo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
